// File: src/pages/registration/RegisterSteps.js

import React from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar } from 'react-icons/fi';
import { LuKeyRound, LuPencil } from 'react-icons/lu';
import axios from 'axios';

import {
  USER_TERMS_OF_SERVICE,
  USER_PRIVACY_POLICY,
} from '../../components/legal/userLegalText';

import {
  getStepIcon,
} from './StepIcons';

import {
  getPrimaryButtonStyle,
  getSecondaryButtonStyle,
  labelStyle,
  inputStyle,
  highlightStyle,
  alreadyHaveAccountStyle,
  loginLinkStyle,
  brandTitleStyleLogin,
  formContainerWide,
  formRow2Col,
  labelUpperLogin,
  inputWideLogin,
  headingCircleContainer,
  stepHeadingStyle,
  stepSubTitleStyle,
  stepContainerNarrow,
  codeInputsRowStyle,
  getCodeDigitInputStyle,
  resendTextStyle,
  resendLinkStyle,
  containerStyle,
} from './RegisterStyles';

const INTERESTS_STYLES = `
  .registration-interests {
    padding-bottom: calc(120px + env(safe-area-inset-bottom, 0px));
  }

  .registration-interests-count {
    margin: 0 auto 8px;
    text-align: center;
    color: #6b7280;
    font-size: 12px;
    line-height: 1.4;
  }

  .registration-interests-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px 8px;
    margin: 12px 0 20px;
  }

  .registration-interest {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    max-width: 100%;
    min-height: 42px;
    padding: 8px 12px;
    border: 1px solid #dadada;
    border-radius: 999px;
    background: #fff;
    color: #181818;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    box-sizing: border-box;
    cursor: pointer;
    scroll-margin-bottom: 140px;
    transition: background-color 0.12s, border-color 0.12s;
    -webkit-tap-highlight-color: transparent;
  }

  .registration-interest:hover {
    border-color: #a4a4a4;
  }

  .registration-interest-selected,
  .registration-interest-selected:hover {
    border-color: #171717;
    background: #f1f1f1;
  }

  .registration-interest:disabled:not(.registration-interest-selected) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .registration-interest > span:last-child {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .registration-interest-emoji {
    font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
    font-size: 16px;
    line-height: 1.1;
    flex-shrink: 0;
  }

  .registration-interests :focus-visible {
    outline: 2px solid #53534d;
    outline-offset: 3px;
  }

  .registration-interests-custom {
    margin: 20px 0;
    text-align: left;
    scroll-margin-bottom: 140px;
  }

  .registration-interests-custom-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .registration-interests-custom-heading > span {
    color: #9ca3af;
    font-size: 11px;
    white-space: nowrap;
  }

  .registration-interests-custom-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .registration-interests-custom-row input {
    scroll-margin-bottom: 140px;
  }

  .registration-interests-actions {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 102;
    background: #fff;
    border-top: 1px solid #ededed;
    padding: 10px 0 calc(14px + env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
  }

  .registration-interests-action-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 90%;
    max-width: 460px;
    margin: 0 auto;
  }

  .registration-interests-action-row button {
    min-width: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .registration-interest {
      transition: none;
    }
  }
`;

// Use registration’s code-cell styles for both verification steps.
export function VerificationCodeFields({ value, onChange, length = 3, error = false, disabled = false, label = 'Email verification code' }) {
  const inputs = React.useRef([]);
  const digits = Array.from({ length }, (_, index) => value[index] || '');
  const focus = index => {
    const move = () => inputs.current[Math.max(0, Math.min(length - 1, index))]?.focus();
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(move); else move();
  };
  const firstEmpty = () => Math.max(0, digits.findIndex(digit => !digit));
  function replace(index, raw) {
    const clean = raw.replace(/\D/g, '');
    if (clean.length > 1) {
      const next = clean.length >= length ? clean.slice(0, length) : value.slice(0, index) + clean;
      onChange(next.slice(0, length)); focus(Math.min(next.length, length - 1)); return;
    }
    if (index > value.length) { focus(firstEmpty()); return; }
    onChange(clean ? (value.slice(0, index) + clean + value.slice(index + 1)).slice(0, length) : value.slice(0, index));
    if (clean && index < length - 1) focus(index + 1);
  }
  return <>
    <style>{`.registration-code-input::placeholder{color:#9ca3af;font-weight:650;opacity:1}.registration-resend-action{border:0;background:transparent;padding:0;font:inherit;text-decoration:none}.registration-resend-action:disabled{opacity:.55}`}</style>
    <div role="group" aria-label={label}
    style={codeInputsRowStyle}>
    {digits.map((digit, index) => <input key={index} ref={node => { inputs.current[index] = node; }}
      className="registration-code-input" aria-label={`Digit ${index + 1} of ${length}`} type="text" inputMode="numeric" pattern="[0-9]*"
      autoComplete={index === 0 ? 'one-time-code' : 'off'} maxLength={index === 0 ? length : 1} value={digit} placeholder="0" disabled={disabled}
      onChange={event => replace(index, event.target.value)}
      onFocus={() => { if (index > value.length) focus(firstEmpty()); }}
      onKeyDown={event => {
        if (event.key === 'Backspace' && !digit && index > 0) { event.preventDefault(); onChange(value.slice(0, index - 1)); focus(index - 1); }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); focus(index + (event.key === 'ArrowLeft' ? -1 : 1)); }
      }}
      onPaste={event => { event.preventDefault(); const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length); if (pasted) { onChange(pasted); focus(Math.min(pasted.length, length - 1)); } }}
      style={getCodeDigitInputStyle({ error, filled: Boolean(digit) })} />)}
  </div></>;
}

function CelebrationTitle() {
  const emojiRef = React.useRef(null);

  const launchConfetti = () => {
    const emoji = emojiRef.current;

    if (!emoji) return;

    const rect = emoji.getBoundingClientRect();
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + rect.height / 2;

    const colors = [
      '#0b0b0b',
      '#FFD84D',
      '#FF7A59',
      '#5B8DEF',
      '#64C987',
    ];

    for (let i = 0; i < 55; i += 1) {
      const piece = document.createElement('span');

      const angle = Math.random() * Math.PI * 2;
      const distance = 70 + Math.random() * 130;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      piece.style.position = 'fixed';
      piece.style.left = `${originX}px`;
      piece.style.top = `${originY}px`;
      piece.style.width = `${5 + Math.random() * 5}px`;
      piece.style.height = `${7 + Math.random() * 6}px`;
      piece.style.background =
        colors[Math.floor(Math.random() * colors.length)];
      piece.style.borderRadius =
        Math.random() > 0.5 ? '50%' : '2px';
      piece.style.pointerEvents = 'none';
      piece.style.zIndex = '999999';
      piece.style.opacity = '1';
      piece.style.transform =
        `translate(-50%, -50%) rotate(${Math.random() * 360}deg)`;
      piece.style.transition =
        'transform 850ms cubic-bezier(.12,.78,.28,1), opacity 850ms ease';

      document.body.appendChild(piece);

      requestAnimationFrame(() => {
        piece.style.transform =
          `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) ` +
          `rotate(${360 + Math.random() * 540}deg)`;

        piece.style.opacity = '0';
      });

      window.setTimeout(() => {
        piece.remove();
      }, 900);
    }
  };

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      launchConfetti();
    }, 250);

    return () => {
      window.clearTimeout(timeout);
    };
  }, []);

  return (
    <div style={brandTitleStyleLogin}>
      BECOME A MEMBER

      <button
        ref={emojiRef}
        type="button"
        onClick={launchConfetti}
        aria-label="Celebrate"
        style={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          marginLeft: 10,
          fontSize: '1.75rem',
          lineHeight: 1,
          cursor: 'pointer',
          verticalAlign: 'middle',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        🎉
      </button>
    </div>
  );
}

export function renderProgressIndicator(current, total) {
  const step = Math.max(1, Math.min(current || 1, total || 1));
  const totalSteps = Math.max(total || 1, 1);

  return (
    <div
      style={{
        color: '#0b0b0b',
        fontSize: '0.9rem',
        marginRight:10,
        fontWeight: 750,
        lineHeight: 1,
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
        fontFamily:
          "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      {step} of {totalSteps}
    </div>
  );
}

export function ProgressPill({
  step,
  total,
}) {
  return renderProgressIndicator(
    step,
    total
  );
}

export function renderStepContent(
  props
) {
  const {
    step,

    firstName,
    setFirstName,

    lastName,
    setLastName,

    email,
    setEmail,
    useApplicationPhoto = false,

    inviteCode,
    setInviteCode,

    verificationCode,
    setVerificationCode,

    setIsCodeValid,

    shakeInputs,
    setShakeInputs,

    password,
    setPassword,

    confirmPassword,
    setConfirmPassword,

    phoneNumber,
    setPhoneNumber,
    phoneCode, setPhoneCode, phoneBusy, phoneWaitSeconds, phoneChallenge, requestPhoneVerification,

    smsOptIn,
    setSmsOptIn,
    showSmsTermsReminder,
    setShowSmsTermsReminder,
    handleSmsConsentChoice,
    handleSmsConsentContinue,

    segFocus,
    setSegFocus,

    anyBoxFocused,
    moveFocus,

    gender,
    showGenderMenu,
    setShowGenderMenu,

    genderLabel,
    handleGenderSelect,

    dateOfBirth,
    typedBirthday,
    handleBirthdayInput,
    validateAgeAtLeast18,

    profilePicFile,
    profilePicPreview,
    fileInputRef,

    handleProfilePicChange,

    setShowProfileActionSheet,
    renderProfileActionSheet,

    interests,
    setInterests,

    customInterest,
    setCustomInterest,

    checkedTerms,
    setCheckedTerms,

    handleNext,
    handleBack,

    handleSendOrResendCode,

    navigate,
    addToast,
    isSubmitting,
    isSendingCode,
  } = props;

  const themeMetaTag =
    document.querySelector(
      'meta[name="theme-color"]'
    );

  if (themeMetaTag) {
    themeMetaTag.content =
      props.showProfileActionSheet
        ? '#999998'
        : '#ffffff';
  }

  const step1Active = Boolean(
    inviteCode.trim()
  );

  const step2Active = Boolean(
    firstName.trim() &&
      lastName.trim() &&
      email.trim()
  );

  const step4Active = Boolean(
    password.trim() &&
      confirmPassword.trim() &&
      password ===
        confirmPassword
  );

  const step5Active =
    phoneNumber
      .replace(/\D/g, '')
      .length === 10;

  const step6Active = Boolean(
    gender &&
      dateOfBirth &&
      validateAgeAtLeast18 &&
      validateAgeAtLeast18(
        dateOfBirth
      )
  );

  function getAllBoxesBorderColor() {
    if (
      phoneNumber.length ===
      10
    ) {
      return '#007bff';
    }

    if (
      phoneNumber.length ===
      0
    ) {
      return '#ccc';
    }

    if (
      anyBoxFocused()
    ) {
      return '#ccc';
    }

    return 'red';
  }

  function addInterest(
    value
  ) {
    const trimmed =
      value.trim();

    if (!trimmed) {
      return;
    }

    if (
      interests.includes(
        trimmed
      )
    ) {
      return;
    }

    if (
      interests.length >=
      10
    ) {
      addToast(
        'Max 10 interests!',
        'error'
      );

      return;
    }

    setInterests([
      ...interests,
      trimmed,
    ]);
  }

  function removeInterest(
    value
  ) {
    setInterests(
      interests.filter(
        (interest) =>
          interest !== value
      )
    );
  }

  switch (step) {
    case 1:
      return (
        <div
          className={
            shakeInputs
              ? 'shake'
              : ''
          }
          style={{
            display: 'flex',
            flexDirection:
              'column',
          }}
        >
          <div
            style={{
              flex: 1,
            }}
          >
          <CelebrationTitle />
            <p
              style={
                stepSubTitleStyle
              }
            >
        Tribe is{' '}
<strong
  style={{
    color: '#0b0b0b',
    fontWeight: 850,
  }}
>
  invite-only.
</strong>{' '}
Sign up with an invite code, or apply for membership.
</p>
            <div
              style={
                formContainerWide
              }
            >
              <div
                style={{
                  marginBottom:
                    '1rem',
                }}
              >
                <label
                  style={
                    labelUpperLogin
                  }
                >
                  Invite Code
                </label>

                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 320,
                  }}
                >
                  <LuKeyRound
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 14,
                      top: '50%',
                      transform:
                        'translateY(-50%)',
                      fontSize: '1.05rem',
                      color: '#0b0b0b',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />

                  <input
                    type="text"
                    style={{
                      ...inputWideLogin,
                      paddingLeft: 43,
                      marginBottom: 0,
                    }}
                    placeholder="Enter your invite code"
                    value={
                      inviteCode
                    }
                    autoFocus
                    autoCapitalize="characters"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={
                      false
                    }
                    onChange={(
                      event
                    ) => {
                      setInviteCode(
                        event.target.value
                          .trimStart()
                          .toUpperCase()
                      );

                      if (
                        shakeInputs
                      ) {
                        setShakeInputs(
                          false
                        );
                      }
                    }}
                    onKeyDown={(
                      event
                    ) => {
                      if (
                        event.key ===
                          'Enter' &&
                        step1Active
                      ) {
                        handleNext();
                      }
                    }}
                  />
                </div>

             
              </div>

              <button
                type="button"
                onClick={
                  handleNext
                }
                disabled={
                  !step1Active
                }
                style={{
                  ...getPrimaryButtonStyle(
                    step1Active,
                    320
                  ),

                  marginTop:
                    '1rem',
                }}
              >
                Continue
              </button>

              <div
                style={
                  alreadyHaveAccountStyle
                }
              >
                No invite code?
                <Link
                  to="/apply"
                  style={{
                    ...loginLinkStyle,
                    textDecoration: 'none',
                  }}
                >
                  Apply for membership
                </Link>
              </div>
            </div>
          </div>
        </div>
      );

    case 2:
      return (
        <div
          className={
            shakeInputs
              ? 'shake'
              : ''
          }
          style={
            containerStyle
          }
        >
          <div
            style={
              headingCircleContainer
            }
          >
            {getStepIcon(
              step
            )}
          </div>

          <h2
            style={
              stepHeadingStyle
            }
          >
            Basic Info
          </h2>

          <p
            style={
              stepSubTitleStyle
            }
          >
        Add your name and email to continue.
          </p>

          <div
            style={
              formContainerWide
            }
          >
            <div
              style={{
                ...formRow2Col,

                marginBottom:
                  '1rem',
              }}
            >
              <div>
                <label
                  style={
                    labelUpperLogin
                  }
                >
                  First Name
                </label>

                <input
                  type="text"
                  style={
                    inputWideLogin
                  }
                  placeholder="Johnny"
                  value={
                    firstName
                  }
                  onChange={(
                    event
                  ) => {
                    const typed =
                      event.target
                        .value;

                    setFirstName(
                      typed
                        .charAt(0)
                        .toUpperCase() +
                        typed.slice(
                          1
                        )
                    );

                    if (
                      shakeInputs
                    ) {
                      setShakeInputs(
                        false
                      );
                    }
                  }}
                />
              </div>

              <div>
                <label
                  style={
                    labelUpperLogin
                  }
                >
                  Last Name
                </label>

                <input
                  type="text"
                  style={
                    inputWideLogin
                  }
                  placeholder="Appleseed"
                  value={
                    lastName
                  }
                  onChange={(
                    event
                  ) => {
                    const typed =
                      event.target
                        .value;

                    setLastName(
                      typed
                        .charAt(0)
                        .toUpperCase() +
                        typed.slice(
                          1
                        )
                    );

                    if (
                      shakeInputs
                    ) {
                      setShakeInputs(
                        false
                      );
                    }
                  }}
                />
              </div>
            </div>

            <div
              style={{
                marginBottom:
                  '1rem',
              }}
            >
              <label
                style={
                  labelUpperLogin
                }
              >
                Email
              </label>

              <input
                type="email"
                style={
                  inputWideLogin
                }
                placeholder="you@example.com"
                value={email}
                onChange={(
                  event
                ) => {
                  setEmail(
                    event.target.value
                  );

                  if (
                    shakeInputs
                  ) {
                    setShakeInputs(
                      false
                    );
                  }
                }}
                onKeyDown={(
                  event
                ) => {
                  if (
                    event.key ===
                      'Enter' &&
                    step2Active
                  ) {
                    handleNext();
                  }
                }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={
              handleNext
            }
            disabled={
              !step2Active ||
              isSendingCode
            }
            style={getPrimaryButtonStyle(
              step2Active &&
                !isSendingCode,
              320
            )}
          >
            {isSendingCode
              ? 'Sending…'
              : 'Continue'}
          </button>

          <button
            type="button"
            onClick={
              handleBack
            }
            disabled={
              isSendingCode
            }
            style={{
              ...getSecondaryButtonStyle(
                320
              ),
              opacity:
                isSendingCode
                  ? 0.6
                  : 1,
              cursor:
                isSendingCode
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            Back
          </button>
        </div>
      );

    case 3: {
  const code = verificationCode || '';

  const digits = Array.from(
    { length: 3 },
    (_, index) => code[index] || ''
  );

  function focusCodeInput(index) {
    requestAnimationFrame(() => {
      document
        .getElementById(`registration-code-${index}`)
        ?.focus();
    });
  }

  function getFirstIncompleteIndex() {
    const firstEmpty = digits.findIndex((digit) => !digit);

    return firstEmpty === -1
      ? digits.length - 1
      : firstEmpty;
  }

  function handleDigitChange(event, index) {
    if (index > 0 && !digits[index - 1]) {
      focusCodeInput(getFirstIncompleteIndex());
      return;
    }

    const typedDigits = event.target.value.replace(/\D/g, '');

    if (!typedDigits) {
      const updatedDigits = [...digits];

      updatedDigits[index] = '';

      for (
        let nextIndex = index + 1;
        nextIndex < updatedDigits.length;
        nextIndex += 1
      ) {
        updatedDigits[nextIndex] = '';
      }

      setVerificationCode(updatedDigits.join(''));
      setIsCodeValid(false);
      return;
    }

    const newCharacter = typedDigits.slice(-1);
    const updatedDigits = [...digits];

    updatedDigits[index] = newCharacter;

    setVerificationCode(updatedDigits.join(''));
    setIsCodeValid(false);

    if (index < 2) {
      focusCodeInput(index + 1);
    }
  }

  function handleDigitKeyDown(event, index) {
    if (
      event.key === 'Backspace' &&
      !digits[index] &&
      index > 0
    ) {
      event.preventDefault();

      const updatedDigits = [...digits];

      updatedDigits[index - 1] = '';

      for (
        let nextIndex = index;
        nextIndex < updatedDigits.length;
        nextIndex += 1
      ) {
        updatedDigits[nextIndex] = '';
      }

      setVerificationCode(updatedDigits.join(''));
      setIsCodeValid(false);
      focusCodeInput(index - 1);
    }
  }

  function handleCodePaste(event) {
    event.preventDefault();

    const pastedDigits = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 3);

    if (!pastedDigits) {
      return;
    }

    setVerificationCode(pastedDigits);
    setIsCodeValid(false);

    focusCodeInput(
      Math.max(
        0,
        Math.min(pastedDigits.length, 3) - 1
      )
    );
  }

  return (
    <div style={stepContainerNarrow}>
      <style>
        {`
          .registration-code-input::placeholder {
            color: #9ca3af;
            font-weight: 650;
            opacity: 1;
          }
        `}
      </style>

      <div style={headingCircleContainer}>
        {getStepIcon(step)}
      </div>

      <h2 style={stepHeadingStyle}>
        Verification Code
      </h2>

      <p style={stepSubTitleStyle}>
        Enter the 3-digit code we sent to
        <br />

        <span
          className="ph-mask"
          style={highlightStyle}
        >
          {email}
        </span>
      </p>

      <div
        className={shakeInputs ? 'shake' : ''}
        style={codeInputsRowStyle}
      >
        {digits.map((digit, index) => {
          const previousIncomplete =
            index > 0 && !digits[index - 1];

          return (
            <input
              key={index}
              id={`registration-code-${index}`}
              className="registration-code-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={
                index === 0
                  ? 'one-time-code'
                  : 'off'
              }
              maxLength={1}
              value={digit}
              placeholder="0"
              autoFocus={index === 0}
              onFocus={() => {
                if (previousIncomplete) {
                  focusCodeInput(
                    getFirstIncompleteIndex()
                  );
                }
              }}
              onMouseDown={(event) => {
                if (previousIncomplete) {
                  event.preventDefault();

                  focusCodeInput(
                    getFirstIncompleteIndex()
                  );
                }
              }}
              onChange={(event) => {
                handleDigitChange(event, index);
              }}
              onKeyDown={(event) => {
                handleDigitKeyDown(event, index);
              }}
              onPaste={handleCodePaste}
              style={getCodeDigitInputStyle({
                error: Boolean(shakeInputs),
                filled: Boolean(digit),
              })}
            />
          );
        })}
      </div>

      <p style={resendTextStyle}>
        Didn’t get the code?{' '}

        <span
          onClick={
            isSendingCode
              ? undefined
              : handleSendOrResendCode
          }
          style={{
            ...resendLinkStyle,
            opacity: isSendingCode ? 0.55 : 1,
            cursor: isSendingCode
              ? 'not-allowed'
              : 'pointer',
          }}
        >
          {isSendingCode
            ? 'Sending…'
            : 'Click to resend'}
        </span>
      </p>

      <button
        type="button"
        onClick={async () => {
          if (code.length < 3) {
            return;
          }

          try {
            await axios.post(
              `${process.env.REACT_APP_API_URL}/users/verifyCodeInMem`,
              {
                email,
                code,
              }
            );

            setIsCodeValid(true);
            handleNext();
          } catch (err) {
            setIsCodeValid(false);
            setShakeInputs(true);

            setTimeout(() => {
              setShakeInputs(false);
              setVerificationCode('');

              document
                .getElementById(
                  'registration-code-0'
                )
                ?.focus();
            }, 300);

            addToast(
              err.response?.data?.message ||
                'Invalid code',
              'error'
            );
          }
        }}
        disabled={code.length < 3}
        style={getPrimaryButtonStyle(
          code.length === 3
        )}
      >
        Continue
      </button>

      <button
        type="button"
        onClick={handleBack}
        style={getSecondaryButtonStyle()}
      >
        Back
      </button>
    </div>
  );
}
    case 4: {
      const bothTyped =
        Boolean(
          password.trim() &&
            confirmPassword.trim()
        );

      return (
        <div
          className={
            shakeInputs
              ? 'shake'
              : ''
          }
          style={
            containerStyle
          }
        >
          <div
            style={
              headingCircleContainer
            }
          >
            {getStepIcon(
              step
            )}
          </div>

          <h2
            style={
              stepHeadingStyle
            }
          >
            Set Password
          </h2>

          <p
            style={
              stepSubTitleStyle
            }
          >
            Create a secure
            password
          </p>

          <div
            style={
              formContainerWide
            }
          >
            <div
              style={{
                marginBottom:
                  '1rem',

                textAlign:
                  'left',
              }}
            >
              <label
                style={
                  labelStyle
                }
              >
                Password
              </label>

              <input
                id="passwordField"
                type="password"
                placeholder="••••••"
                style={{
                  ...inputStyle,

                  border:
                    `1px solid ${
                      shakeInputs
                        ? '#EF4444'
                        : '#e5e7eb'
                    }`,
                }}
                value={
                  password
                }
                onChange={(
                  event
                ) => {
                  setPassword(
                    event.target
                      .value
                  );

                  if (
                    shakeInputs
                  ) {
                    setShakeInputs(
                      false
                    );
                  }
                }}
              />
            </div>

            <div
              style={{
                marginBottom:
                  '1.25rem',

                textAlign:
                  'left',
              }}
            >
              <label
                style={
                  labelStyle
                }
              >
                Re-enter Password
              </label>

              <input
                id="confirmPasswordField"
                type="password"
                placeholder="••••••"
                style={{
                  ...inputStyle,

                  border:
                    `1px solid ${
                      shakeInputs
                        ? '#EF4444'
                        : '#e5e7eb'
                    }`,
                }}
                value={
                  confirmPassword
                }
                onChange={(
                  event
                ) => {
                  setConfirmPassword(
                    event.target
                      .value
                  );

                  if (
                    shakeInputs
                  ) {
                    setShakeInputs(
                      false
                    );
                  }
                }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (!bothTyped) {
                return;
              }

              if (
                password ===
                confirmPassword
              ) {
                handleNext();
                return;
              }

              addToast(
                'Passwords do not match!',
                'error'
              );

              setShakeInputs(
                true
              );

              setTimeout(() => {
                setShakeInputs(
                  false
                );

                setPassword('');
                setConfirmPassword('');

                document
                  .getElementById(
                    'passwordField'
                  )
                  ?.focus();
              }, 300);
            }}
            disabled={
              !bothTyped
            }
            style={getPrimaryButtonStyle(
              bothTyped
            )}
          >
            Continue
          </button>

          <button
            type="button"
            onClick={
              handleBack
            }
            style={getSecondaryButtonStyle()}
          >
            Back
          </button>
        </div>
      );
    }

    case 5: {
      const digits =
        phoneNumber
          .replace(/\D/g, '')
          .slice(0, 10);

      const segment2 =
        digits.slice(0, 3);

      const segment3 =
        digits.slice(3, 6);

      const segment4 =
        digits.slice(6, 10);

      function handleSegmentChange(
        segment,
        rawValue
      ) {
        const cleaned =
          rawValue.replace(
            /\D/g,
            ''
          );

        let nextSegment2 =
          segment2;

        let nextSegment3 =
          segment3;

        let nextSegment4 =
          segment4;

        if (
          segment === 'seg2'
        ) {
          nextSegment2 =
            cleaned.slice(0, 3);
        }

        if (
          segment === 'seg3'
        ) {
          nextSegment3 =
            cleaned.slice(0, 3);
        }

        if (
          segment === 'seg4'
        ) {
          nextSegment4 =
            cleaned.slice(0, 4);
        }

        setPhoneNumber(
          nextSegment2 +
            nextSegment3 +
            nextSegment4
        );
      }

      const phoneBorder =
        `1px solid ${getAllBoxesBorderColor()}`;

      return (
        <div
          style={
            containerStyle
          }
        >
          <div
            style={
              headingCircleContainer
            }
          >
            {getStepIcon(
              step
            )}
          </div>

          <h2
            style={
              stepHeadingStyle
            }
          >
            Phone Number
          </h2>

          <p
            style={
              stepSubTitleStyle
            }
          >
            Please provide your
            10-digit phone number
          </p>

          <div
            style={{
              display:
                'flex',

              justifyContent:
                'center',

              gap:
                '0.5rem',

              marginBottom:
                '1.5rem',
            }}
          >
            <input
              type="text"
              value="+1"
              readOnly
              style={{
                ...inputStyle,

                width:
                  45,

                textAlign:
                  'center',

                backgroundColor:
                  '#f3f4f6',

                color:
                  '#9ca3af',

                border:
                  '0px solid transparent',
              }}
            />

            <input
              id="seg2"
              type="tel"
              inputMode="tel"
              pattern="[0-9]*"
              autoComplete="off"
              placeholder="xxx"
              value={
                segment2
              }
              onFocus={() => {
                setSegFocus({
                  ...segFocus,
                  seg2: true,
                });
              }}
              onBlur={() => {
                setSegFocus({
                  ...segFocus,
                  seg2: false,
                });
              }}
              onKeyDown={(
                event
              ) => {
                if (
                  /[0-9]/.test(
                    event.key
                  ) &&
                  segment2.length ===
                    3
                ) {
                  event.preventDefault();

                  moveFocus(
                    'seg2',
                    1
                  );
                }
              }}
              onChange={(
                event
              ) => {
                handleSegmentChange(
                  'seg2',
                  event.target.value
                );

                if (
                  event.target.value
                    .replace(
                      /\D/g,
                      ''
                    )
                    .length === 3
                ) {
                  moveFocus(
                    'seg2',
                    1
                  );
                }
              }}
              style={{
                ...inputStyle,

                width:
                  70,

                textAlign:
                  'center',

                border:
                  phoneBorder,
              }}
            />

            <input
              id="seg3"
              type="tel"
              inputMode="tel"
              pattern="[0-9]*"
              autoComplete="off"
              placeholder="xxx"
              value={
                segment3
              }
              onFocus={() => {
                setSegFocus({
                  ...segFocus,
                  seg3: true,
                });
              }}
              onBlur={() => {
                setSegFocus({
                  ...segFocus,
                  seg3: false,
                });
              }}
              onKeyDown={(
                event
              ) => {
                if (
                  event.key ===
                    'Backspace' &&
                  segment3.length ===
                    0
                ) {
                  event.preventDefault();

                  moveFocus(
                    'seg3',
                    -1
                  );
                }

                if (
                  /[0-9]/.test(
                    event.key
                  ) &&
                  segment3.length ===
                    3
                ) {
                  event.preventDefault();

                  moveFocus(
                    'seg3',
                    1
                  );
                }
              }}
              onChange={(
                event
              ) => {
                handleSegmentChange(
                  'seg3',
                  event.target.value
                );

                if (
                  event.target.value
                    .replace(
                      /\D/g,
                      ''
                    )
                    .length === 3
                ) {
                  moveFocus(
                    'seg3',
                    1
                  );
                }
              }}
              style={{
                ...inputStyle,

                width:
                  70,

                textAlign:
                  'center',

                border:
                  phoneBorder,
              }}
            />

            <input
              id="seg4"
              type="tel"
              inputMode="tel"
              pattern="[0-9]*"
              autoComplete="off"
              placeholder="xxxx"
              value={
                segment4
              }
              onFocus={() => {
                setSegFocus({
                  ...segFocus,
                  seg4: true,
                });
              }}
              onBlur={() => {
                setSegFocus({
                  ...segFocus,
                  seg4: false,
                });
              }}
              onKeyDown={(
                event
              ) => {
                if (
                  event.key ===
                    'Backspace' &&
                  segment4.length ===
                    0
                ) {
                  event.preventDefault();

                  moveFocus(
                    'seg4',
                    -1
                  );
                }
              }}
              onChange={(
                event
              ) => {
                handleSegmentChange(
                  'seg4',
                  event.target.value
                );
              }}
              style={{
                ...inputStyle,

                width:
                  90,

                textAlign:
                  'center',

                border:
                  phoneBorder,
              }}
            />
          </div>

          <button
            type="button"
            onClick={
              handleNext
            }
            disabled={
              !step5Active || phoneBusy
            }
            style={getPrimaryButtonStyle(
              step5Active
            )}
          >
            {phoneBusy ? 'Sending code…' : 'Continue'}
          </button>

          <button
            type="button"
            onClick={
              handleBack
            }
            style={getSecondaryButtonStyle()}
          >
            Back
          </button>
        </div>
      );
    }


    case 5.5: {
      const canVerify = !phoneBusy && Boolean(phoneChallenge) && /^\d{3}$/.test(phoneCode);
      return <div style={stepContainerNarrow}>
        <div style={headingCircleContainer}>{getStepIcon(5)}</div>
        <h2 style={stepHeadingStyle}>Verification Code</h2>
        <p style={stepSubTitleStyle}>Enter the 3-digit code we texted to<br />
          <span className="ph-mask" style={highlightStyle}>+1 {phoneNumber}</span>
        </p>
        <VerificationCodeFields value={phoneCode} onChange={setPhoneCode} length={3} disabled={phoneBusy} label="Phone verification code" />
        <p style={resendTextStyle}>Didn’t get the code?{' '}
          <button type="button" className="registration-resend-action" style={resendLinkStyle}
            disabled={phoneBusy || phoneWaitSeconds > 0} onClick={() => requestPhoneVerification(false)}>
            {phoneWaitSeconds ? `Resend in ${phoneWaitSeconds}s` : 'Click to resend'}
          </button>
        </p>
        <button type="button" disabled={!canVerify} onClick={handleNext} style={getPrimaryButtonStyle(canVerify)}>
          {phoneBusy ? 'Verifying…' : 'Continue'}
        </button>
        <button type="button" disabled={phoneBusy} onClick={handleBack} style={getSecondaryButtonStyle()}>Back</button>
      </div>;
    }

    case 6: {
      const smsChoiceMade =
        smsOptIn === true ||
        smsOptIn === false;

      const smsYesSelected =
        smsOptIn === true;

      const smsNoSelected =
        smsOptIn === false;

      return (
        <div
          style={
            containerStyle
          }
        >
          <div
            style={
              headingCircleContainer
            }
          >
            {getStepIcon(
              step
            )}
          </div>

          <h2
            style={
              stepHeadingStyle
            }
          >
            Text Notifications
          </h2>

          <p
            style={{
              ...stepSubTitleStyle,
              maxWidth: 340,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
We use text messages to coordinate your Tribe experience - including personalized recommendations, reservation reminders, and important updates for gatherings you reserve.          </p>

          <div
            style={{
              width: '100%',
              maxWidth: 320,
              margin: '0 auto',
            }}
          >
            <button
              type="button"
              onClick={() => {
                handleSmsConsentChoice?.(
                  true
                );
              }}
              style={{
                width: '100%',
                minHeight: 52,
                padding: '10px 12px',
                border:
                  smsYesSelected
                    ? '1.5px solid #0b0b0b'
                    : '1px solid #d1d5db',
                borderRadius: 12,
                background:
                  smsYesSelected
                    ? '#f7f7f7'
                    : '#fff',
                color: '#0b0b0b',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  'space-between',
                gap: 10,
                boxSizing:
                  'border-box',
                fontFamily:
                  "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                WebkitTapHighlightColor:
                  'transparent',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    borderRadius: '50%',
                    border:
                      '1.5px solid #0b0b0b',
                    boxSizing:
                      'border-box',
                    background:
                      smsYesSelected
                        ? '#0b0b0b'
                        : '#fff',
                    boxShadow:
                      smsYesSelected
                        ? 'inset 0 0 0 4px #fff'
                        : 'none',
                  }}
                />

                <span
                  style={{
                    fontSize: 13,
                    fontWeight:
                      smsYesSelected
                        ? 800
                        : 750,
                    lineHeight: 1.2,
                    textAlign: 'left',
                  }}
                >
                  Yes, enable text updates
                </span>
              </span>

              <span
                style={{
                  flexShrink: 0,
                  padding: '4px 7px',
                  borderRadius: 999,
                  background:
                    '#0b0b0b',
                  color: '#fff',
                  fontSize: 9.5,
                  lineHeight: 1,
                  fontWeight: 800,
                  letterSpacing:
                    '0.01em',
                }}
              >
                Recommended
              </span>
            </button>

            {!smsYesSelected && (
              <button
                type="button"
                onClick={() => {
                  handleSmsConsentChoice?.(
                    false
                  );
                }}
                style={{
                  width: '100%',
                  minHeight: 52,
                  padding: '10px 12px',
                  marginTop: 10,
                  border:
                    smsNoSelected
                      ? '1.5px solid #0b0b0b'
                      : '1px solid #e5e7eb',
                  borderRadius: 12,
                  background:
                    smsNoSelected
                      ? '#f7f7f7'
                      : '#fff',
                  color: '#0b0b0b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  boxSizing:
                    'border-box',
                  fontFamily:
                    "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                  WebkitTapHighlightColor:
                    'transparent',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    borderRadius: '50%',
                    border:
                      '1.5px solid #0b0b0b',
                    boxSizing:
                      'border-box',
                    background:
                      smsNoSelected
                        ? '#0b0b0b'
                        : '#fff',
                    boxShadow:
                      smsNoSelected
                        ? 'inset 0 0 0 4px #fff'
                        : 'none',
                  }}
                />

                <span
                  style={{
                    fontSize: 13,
                    fontWeight:
                      smsNoSelected
                        ? 800
                        : 650,
                    lineHeight: 1.2,
                    textAlign: 'left',
                  }}
                >
                  No, not now
                </span>
              </button>
            )}

            <p
              style={{
                maxWidth: 320,
                margin: '1rem auto 0',
                fontSize: '0.72rem',
                lineHeight: 1.45,
                color: '#6b7280',
                textAlign: 'left',
              }}
            >
              By enabling text updates, you agree to receive recurring SMS messages from Tribe. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe.{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: '#0b0b0b',
                  fontWeight: 700,
                  textDecoration:
                    'underline',
                }}
              >
                Terms
              </a>
              {' · '}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: '#0b0b0b',
                  fontWeight: 700,
                  textDecoration:
                    'underline',
                }}
              >
                Privacy
              </a>
            </p>

            <p
              style={{
                maxWidth: 320,
                margin: '0.5rem auto 0',
                fontSize: '0.72rem',
                lineHeight: 1.45,
                color: '#9ca3af',
                textAlign: 'left',
              }}
            >
              Text updates are optional and are not required to use Tribe.
            </p>
          </div>

          {smsChoiceMade && (
            <button
              type="button"
              onClick={
                handleSmsConsentContinue
              }
              style={{
                ...getPrimaryButtonStyle(
                  true,
                  320
                ),
                marginTop: '1rem',
              }}
            >
              Continue
            </button>
          )}

          <button
            type="button"
            onClick={
              handleBack
            }
            style={{
              ...getSecondaryButtonStyle(
                320
              ),
              marginTop:
                smsChoiceMade
                  ? '0.75rem'
                  : '1rem',
            }}
          >
            Back
          </button>
        </div>
      );
    }

    case 7: {
      const canContinue =
        Boolean(
          step6Active &&
            typedBirthday
              ?.length === 10
        );

      return (
        <div
          style={
            containerStyle
          }
        >
          <div
            style={
              headingCircleContainer
            }
          >
            {getStepIcon(
              step
            )}
          </div>

          <h2
            style={
              stepHeadingStyle
            }
          >
            Gender &amp; Birthday
          </h2>

          <p
            style={
              stepSubTitleStyle
            }
          >
            We require this info
            to ensure you’re 18+.
          </p>

          <div
            style={{
              position:
                'relative',

              textAlign:
                'left',

              maxWidth:
                300,

              margin:
                '0 auto 2rem',
            }}
          >
            <label
              style={
                labelStyle
              }
            >
              Gender
            </label>

            <div
              className="custom-dropdown-trigger"
              onClick={() => {
                setShowGenderMenu(
                  !showGenderMenu
                );
              }}
              style={{
                ...inputStyle,

                display:
                  'flex',

                alignItems:
                  'center',

                justifyContent:
                  'space-between',

                height:
                  40,

                cursor:
                  'pointer',

                marginTop:
                  8,
              }}
            >
              <span>
                {genderLabel?.()}
              </span>

              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
              >
                <path
                  d="M0 0L5 6L10 0H0Z"
                  fill="#666"
                />
              </svg>
            </div>

            {showGenderMenu && (
              <ul
                className="custom-dropdown-menu"
                style={{
                  position:
                    'absolute',

                  top:
                    'calc(100% + 8px)',

                  left:
                    0,

                  width:
                    '100%',

                  background:
                    '#fff',

                  border:
                    '1px solid #eee',

                  borderRadius:
                    8,

                  boxShadow:
                    '0 4px 10px rgba(0,0,0,0.1)',

                  zIndex:
                    9999,

                  margin:
                    0,

                  padding:
                    8,

                  listStyle:
                    'none',

                  maxHeight:
                    300,

                  overflowY:
                    'auto',
                }}
              >
                {[
                  {
                    value: '',
                    label:
                      '--Select--',
                  },
                  {
                    value:
                      'male',
                    label:
                      'Male',
                  },
                  {
                    value:
                      'female',
                    label:
                      'Female',
                  },
                  {
                    value:
                      'non-binary',
                    label:
                      'Non-binary',
                  },
                  {
                    value:
                      'trans-man',
                    label:
                      'Trans-Man',
                  },
                  {
                    value:
                      'trans-woman',
                    label:
                      'Trans-Woman',
                  },
                ].map(
                  ({
                    value,
                    label,
                  }) => (
                    <li
                      key={
                        value ||
                        'empty'
                      }
                      onClick={() => {
                        handleGenderSelect(
                          value
                        );
                      }}
                      style={{
                        padding:
                          '8px 12px',

                        cursor:
                          'pointer',

                        borderRadius:
                          8,

                        marginBottom:
                          4,
                      }}
                    >
                      {label}
                    </li>
                  )
                )}
              </ul>
            )}
          </div>

          <div
            style={{
              position:
                'relative',

              textAlign:
                'left',

              maxWidth:
                300,

              margin:
                '25px auto 2rem',
            }}
          >
            <label
              style={
                labelStyle
              }
            >
              Birthday
            </label>

            <input
              type="text"
              inputMode="numeric"
              pattern="\d{2}/\d{2}/\d{4}"
              placeholder="MM/DD/YYYY"
              value={
                typedBirthday ||
                ''
              }
              onChange={(
                event
              ) => {
                handleBirthdayInput(
                  event.target.value
                );
              }}
              style={{
                ...inputStyle,

                paddingRight:
                  40,
              }}
            />

            <div
              style={{
                position:
                  'absolute',

                right:
                  10,

                top:
                  '50%',

                transform:
                  'translateY(-50%)',

                pointerEvents:
                  'none',

                opacity:
                  0.8,
              }}
            >
              <FiCalendar
                style={{
                  fontSize:
                    '1rem',

                  paddingTop:
                    24,
                }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (
                !dateOfBirth ||
                !validateAgeAtLeast18(
                  dateOfBirth
                )
              ) {
                addToast(
                  'You must be at least 18. Please enter a valid birthday.',
                  'error'
                );

                setShakeInputs(
                  true
                );

                setTimeout(() => {
                  setShakeInputs(
                    false
                  );
                }, 300);

                return;
              }

              if (!gender) {
                addToast(
                  'Please select your gender.',
                  'error'
                );

                setShakeInputs(
                  true
                );

                setTimeout(() => {
                  setShakeInputs(
                    false
                  );
                }, 300);

                return;
              }

              handleNext();
            }}
            disabled={
              !gender ||
              !canContinue
            }
            style={getPrimaryButtonStyle(
              Boolean(
                gender &&
                  canContinue
              )
            )}
          >
            Continue
          </button>

          <button
            type="button"
            onClick={
              handleBack
            }
            style={getSecondaryButtonStyle()}
          >
            Back
          </button>
        </div>
      );
    }

    case 8: {
      const active =
        Boolean(
          profilePicFile || useApplicationPhoto
        );

      return (
        <div
          style={
            containerStyle
          }
        >
          <div
            style={
              headingCircleContainer
            }
          >
            {getStepIcon(
              step
            )}
          </div>

          <h2
            style={
              stepHeadingStyle
            }
          >
            Profile Picture
          </h2>

          <p
            style={
              stepSubTitleStyle
            }
          >
            Please upload a JPG/PNG
            under 15MB.
          </p>

          <div
            style={{
              margin:
                '1.5rem auto',

              position:
                'relative',

              display:
                'inline-block',
            }}
          >
            <input
              ref={
                fileInputRef
              }
              type="file"
              accept="image/*"
              onChange={
                handleProfilePicChange
              }
              style={{
                display:
                  'none',
              }}
            />

            <div
              onClick={() => {
                setShowProfileActionSheet(
                  true
                );
              }}
              style={{
                width:
                  140,

                height:
                  140,

                borderRadius:
                  '50%',

                backgroundColor:
                  '#f3f4f6',

                overflow:
                  'hidden',

                position:
                  'relative',

                cursor:
                  'pointer',

                border:
                  '2px solid #e5e7eb',
              }}
            >
              {profilePicPreview && (
                <img
                  src={
                    profilePicPreview
                  }
                  alt="Profile Preview"
                  className="ph-mask"
                  style={{
                    width:
                      '100%',

                    height:
                      '100%',

                    objectFit:
                      'cover',
                  }}
                />
              )}
            </div>

            <div
              onClick={() => {
                setShowProfileActionSheet(
                  true
                );
              }}
              style={{
                position:
                  'absolute',

                bottom:
                  0,

                right:
                  0,

                width:
                  40,

                height:
                  40,

                borderRadius:
                  '50%',

                backgroundColor:
                  '#fff',

                display:
                  'flex',

                alignItems:
                  'center',

                justifyContent:
                  'center',

                cursor:
                  'pointer',

                border:
                  '1px solid #ddd',
              }}
            >
              <LuPencil
                style={{
                  fontSize:
                    '1.1rem',

                  color:
                    '#555',
                }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={
              handleNext
            }
            disabled={
              !active
            }
            style={getPrimaryButtonStyle(
              active
            )}
          >
            Continue
          </button>

          <button
            type="button"
            onClick={
              handleBack
            }
            style={getSecondaryButtonStyle()}
          >
            Back
          </button>

          {renderProfileActionSheet?.()}
        </div>
      );
    }

    case 9: {
      const PRESET_INTERESTS =
        [
          {
            name: 'Hiking',
            emoji: '🥾',
          },
          {
            name: 'Painting',
            emoji: '🎨',
          },
          {
            name: 'Coding',
            emoji: '💻',
          },
          {
            name: 'Music',
            emoji: '🎵',
          },
          {
            name: 'Art',
            emoji: '🖼️',
          },
          {
            name: 'Reading',
            emoji: '📚',
          },
          {
            name: 'Travel',
            emoji: '✈️',
          },
          {
            name:
              'Photography',
            emoji: '📸',
          },
          {
            name: 'Gaming',
            emoji: '🎮',
          },
          {
            name: 'Cooking',
            emoji: '🍳',
          },
          {
            name: 'Yoga',
            emoji: '🧘',
          },
          {
            name: 'Dancing',
            emoji: '💃',
          },
          {
            name: 'Sports',
            emoji: '🏀',
          },
          {
            name: 'Movies',
            emoji: '🎬',
          },
          {
            name: 'Fitness',
            emoji: '🏋️',
          },
          {
            name:
              'Meditation',
            emoji: '🧘',
          },
          {
            name:
              'Board Games',
            emoji: '🎲',
          },
          {
            name:
              'Gardening',
            emoji: '🌱',
          },
        ];

      const INTEREST_CHAR_LIMIT = 25;
      const active = interests.length >= 3;
      const atLimit = interests.length >= 10;
      const trimmedCustom = customInterest.trim();
      const canAdd = Boolean(trimmedCustom && !interests.includes(trimmedCustom) && !atLimit);
      const options = [
        ...PRESET_INTERESTS,
        ...interests
          .filter(name => !PRESET_INTERESTS.some(interest => interest.name === name))
          .map(name => ({ name, emoji: '✨' })),
      ];

      function handleCustomInterest() {
        if (!canAdd) return;
        addInterest(trimmedCustom);
        setCustomInterest('');
      }

      return (
        <div className="registration-interests" style={{ ...containerStyle, width: '100%' }}>
          <style>{INTERESTS_STYLES}</style>

          <div style={headingCircleContainer}>{getStepIcon(step)}</div>
          <h2 style={stepHeadingStyle}>Select Interests</h2>
          <p style={{ ...stepSubTitleStyle, marginBottom: 6 }}>
            Choose 3–10 interests, or add your own.
          </p>
          <p className="registration-interests-count" role="status" aria-live="polite">
            {interests.length} of 10 selected
          </p>

          <div className="registration-interests-custom">
            <div className="registration-interests-custom-heading">
              <label htmlFor="registration-custom-interest" style={{ ...labelUpperLogin, marginBottom: 0 }}>
                Add your own
              </label>
              <span aria-hidden="true">{customInterest.length}/{INTEREST_CHAR_LIMIT}</span>
            </div>
            <div className="registration-interests-custom-row">
              <input
                id="registration-custom-interest"
                type="text"
                className="ph-mask"
                aria-label="Add an interest"
                placeholder="Add an interest…"
                autoComplete="off"
                maxLength={INTEREST_CHAR_LIMIT}
                value={customInterest}
                onChange={event => setCustomInterest(event.target.value.slice(0, INTEREST_CHAR_LIMIT))}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.stopPropagation();
                    handleCustomInterest();
                  }
                }}
                style={{ ...inputWideLogin, flex: 1, minWidth: 0, maxWidth: 'none', margin: 0, fontSize: 16, outline: undefined }}
              />
              <button
                type="button"
                aria-label="Add interest"
                disabled={!canAdd}
                onClick={handleCustomInterest}
                style={{ ...getSecondaryButtonStyle(64), width: 64, height: 48, margin: 0, opacity: canAdd ? 1 : 0.45, cursor: canAdd ? 'pointer' : 'not-allowed' }}
              >
                Add
              </button>
            </div>
          </div>

          <div className="registration-interests-list" role="group" aria-label="Interests">
            {options.map(({ name, emoji }) => {
              const selected = interests.includes(name);
              return (
                <button
                  key={name}
                  type="button"
                  className={`registration-interest${selected ? ' registration-interest-selected' : ''}`}
                  aria-pressed={selected}
                  disabled={!selected && atLimit}
                  onClick={() => selected ? removeInterest(name) : addInterest(name)}
                >
                  <span className="registration-interest-emoji" aria-hidden="true">{emoji}</span>
                  <span>{name}</span>
                </button>
              );
            })}
          </div>

          <div className="registration-interests-actions">
            <div className="registration-interests-action-row">
              <button
                type="button"
                onClick={handleBack}
                style={{ ...getSecondaryButtonStyle(), flex: 1, maxWidth: 'none', margin: 0, height: 42 }}
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={!active}
                style={{ ...getPrimaryButtonStyle(active), flex: 1, maxWidth: 'none', margin: 0 }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      );
    }

    case 10:
      return (
        <div
          style={{
            textAlign:
              'center',

            padding:
              '1rem 1rem',

            maxWidth:
              450,

            margin:
              '0rem auto',
          }}
        >
          <div
            style={
              headingCircleContainer
            }
          >
            {getStepIcon(
              step
            )}
          </div>

          <h2
            style={
              stepHeadingStyle
            }
          >
            Terms &amp; Conditions
          </h2>

          <p
            style={
              stepSubTitleStyle
            }
          >
            Please agree to our
            Terms of Service and
            Privacy Policy before
            creating your account.
          </p>

          <div
            style={{
              marginTop:
                '1rem',

              maxHeight:
                showSmsTermsReminder
                  ? '195px'
                  : '260px',

              overflowY:
                'auto',

              border:
                '1px solid #eee',

              borderRadius:
                8,

              padding:
                '12px 12px',

              fontSize:
                '0.75rem',

              color:
                '#444',

              textAlign:
                'left',

              background:
                '#fff',
            }}
          >
            <div
              style={{
                whiteSpace:
                  'pre-wrap',
              }}
            >
              {
                USER_TERMS_OF_SERVICE
              }

              {'\n\n'}

              {
                USER_PRIVACY_POLICY
              }
            </div>
          </div>

          {showSmsTermsReminder && (
            <div
              style={{
                maxWidth: 360,
                margin: '1rem auto 0',
                textAlign: 'left',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  lineHeight: 1.5,
                  color: '#0b0b0b',
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    smsOptIn === true
                  }
                  onChange={(
                    event
                  ) => {
                    setSmsOptIn?.(
                      event.target.checked
                    );
                  }}
                  style={{
                    marginTop: 3,
                    flexShrink: 0,
                  }}
                />

                <span>
I agree to receive recurring SMS updates from Tribe, including gathering recommendations, reservation reminders, and important gathering updates. Message frequency varies. Msg & data rates may apply. Reply STOP to unsubscribe.                  <a
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(
                      event
                    ) => {
                      event.stopPropagation();
                    }}
                    style={{
                      color: '#0b0b0b',
                      fontWeight: 700,
                      textDecoration:
                        'underline',
                    }}
                  >
                    Terms
                  </a>
                  {' · '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(
                      event
                    ) => {
                      event.stopPropagation();
                    }}
                    style={{
                      color: '#0b0b0b',
                      fontWeight: 700,
                      textDecoration:
                        'underline',
                    }}
                  >
                    Privacy
                  </a>
                </span>
              </label>
            </div>
          )}

          <div
            style={{
              marginTop:
                '1rem',

              textAlign:
                'left',

              maxWidth:
                360,

              margin:
                '1rem auto',
            }}
          >
            <label
              style={{
                display:
                  'flex',

                alignItems:
                  'center',

                gap:
                  '6px',

                cursor:
                  'pointer',

                fontSize:
                  '0.85rem',
              }}
            >
              <input
                type="checkbox"
                checked={
                  checkedTerms
                }
                onChange={(
                  event
                ) => {
                  setCheckedTerms(
                    event.target
                      .checked
                  );
                }}
              />

              <span>
                I agree to the{' '}

                <span
                  style={{
                    fontWeight:
                      700,

                    color:
                      '#000',

                    textDecoration:
                      'underline',
                  }}
                >
                  Terms and
                  Conditions
                </span>{' '}

                and{' '}

                <span
                  style={{
                    fontWeight:
                      700,

                    color:
                      '#000',

                    textDecoration:
                      'underline',
                  }}
                >
                  Privacy Policy
                </span>
                .
              </span>
            </label>
          </div>

          <style>
            {`
              @keyframes spin {
                from {
                  transform: rotate(0deg);
                }

                to {
                  transform: rotate(360deg);
                }
              }
            `}
          </style>

          <button
            type="button"
            onClick={
              handleNext
            }
            disabled={
              !checkedTerms ||
              isSubmitting
            }
            style={{
              ...getPrimaryButtonStyle(
                checkedTerms &&
                  !isSubmitting
              ),

              width:
                '100%',

              maxWidth:
                300,

              margin:
                '2rem auto 0 auto',

              display:
                'flex',

              alignItems:
                'center',

              justifyContent:
                'center',

              gap:
                10,

              opacity:
                isSubmitting
                  ? 0.85
                  : 1,

              cursor:
                isSubmitting
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            {isSubmitting ? (
              <>
                <span
                  style={{
                    width:
                      14,

                    height:
                      14,

                    borderRadius:
                      999,

                    border:
                      '2px solid rgba(255,255,255,0.5)',

                    borderTopColor:
                      '#fff',

                    display:
                      'inline-block',

                    animation:
                      'spin 0.8s linear infinite',
                  }}
                />

                Creating…
              </>
            ) : (
              'Create Account'
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              if (
                isSubmitting
              ) {
                return;
              }

              handleBack();
            }}
            disabled={
              isSubmitting
            }
            style={{
              ...getSecondaryButtonStyle(),

              opacity:
                isSubmitting
                  ? 0.6
                  : 1,

              cursor:
                isSubmitting
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            Back
          </button>
        </div>
      );

    default:
      return (
        <div>
          Unknown step
        </div>
      );
  }
}
